
import React from "react";
import { Avatar, Box, Button, Stack, Theme, alpha } from "@mui/material";
import { GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LockIcon from "@mui/icons-material/Lock";
import moment from "moment";
import { IParlayOutcome, IShopOutcome } from "./store/types";
import { evAgeLabel } from "./optimizerHelper";

export const createOptimizerDesktopColumns = (
  getType: (o: IShopOutcome) => "Add" | "Swap" | "Remove",
  handleLegClick: (o: IParlayOutcome) => void,
  isDisplayUnlocked: boolean,
  isPickemTable: boolean,
  exposureCounts: Map<string, number>,
  theme: Theme,
): GridColDef[] => [
  {
    field: "name",
    headerName: "PLAY",
    flex: 3,
    headerAlign: "left",
    sortable: false,
    renderCell(params: GridRenderCellParams<IShopOutcome>) {
      const { participantLabel, offerName, line, label, awayTeamAbbreviation, homeTeamAbbreviation, eventStartDate, playerTeamId, homeTeamId } = params.row;
      // Back end ISO date string isn't always correct so verify and fix it here if needed.
      const start = eventStartDate.endsWith('Z') ? eventStartDate : `${eventStartDate}Z`;
      return (
        <Stack id='stack-from-columns' direction="row" overflow="hidden">
          {/* These elements below are for V2 so leave commented out for now */}
          {/* <Box
            sx={{
              alignItems: "center",
              justifyContent: "center",
              display: "inline-flex",
              padding: "12px 8px 12px 8px",
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                justifyContent: "center",
                display: "inline-flex",
                width: "20",
                height: "20",
              }}
            >
              <ExpandMore></ExpandMore>
            </Box>
          </Box>
          <Box
            sx={{
              alignItems: "center",
              justifyContent: "center",
              display: "inline-flex",
              padding: "12px 10px 12px 10px",
            }}
          >
            <Box
              sx={{
                border: "1px solid rgba(255, 107, 0, 0.7)",
                borderRadius: 2,
                background: "rgba(255, 107, 0, 0.10)",
                width: 32,
                height: 32,
                justifyContent: "center",
                alignItems: "center",
                display: "inline-flex",
              }}
            >
                <Image src="/assets/img/chart_FILL0_wght200_GRAD0_opsz24.svg" alt="receipt" width={28} height={28} style={{ filter: 'invert(49%) sepia(40%) saturate(4603%) hue-rotate(359deg) brightness(101%) contrast(108%)'}} />
            </Box>
          </Box> */}
          <Box sx={{alignItems: "center",
              justifyContent: "center",
              display: "inline-flex",
              gap: 10,
              paddingRight: '10px',
              }}>
                <Avatar src={params.row.playerParticipantHeadShot} alt={params.row.participantName} sx={{height: 40, width: 40, backgroundColor: alpha(theme.palette.common.white, 0.15)}}/>
          </Box>
          <Stack direction="column" overflow="hidden">
            <Box
              fontSize="16px"
              fontWeight={700}
              sx={{
                textWrap: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {participantLabel}
            </Box>
            <Box>
              {label} {line} {offerName}
            </Box>
            <Box
              sx={[
                (theme) => ({
                  color: alpha(theme.palette.common.white, 0.87),
                  paddingTop: "6px",
                  "text-wrap": "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }),
              ]}
              fontSize="12px"
              fontWeight={400}
            >
              vs {playerTeamId === homeTeamId ? awayTeamAbbreviation : homeTeamAbbreviation} {moment(start).local().format('ddd M/DD @ h:mm A z')}
            </Box>
          </Stack>
        </Stack>
      );
    },
  },
  {
    field: "expectedWinPct",
    headerName: "xWIN",
    headerAlign: "center",
    flex: 1,
    align: "center",
    sortable: true,
    type: "number",
    ...(isPickemTable && {
      headerClassName: "ev",
      cellClassName: "evCell",
    }),
    valueGetter(params) {
      return parseInt(params.value);
    },
    renderCell(params) {
      const lock =
        params.api.getSortedRowIds().indexOf(params.id) > 0 &&
        !isDisplayUnlocked;

      if (lock) {
        return <LockIcon />;
      }
      if (isNaN(params.value)) {
        return "-";
      }
      if (!isPickemTable) {
        return params.value + "%";
      }
      return (
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            fontWeight: 700,
            backgroundColor: "background.secondary"
          }}
        >
          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
              backgroundColor: alpha("#00723B", params.value / 200),
            }}
          >
            {lock ? <LockIcon /> : params.value ? params.value + "%" : "-"}
          </Stack>
        </Stack>
      );
    },
    cellClassName: (params: GridCellParams<IShopOutcome>) => {
      let cellClasses = isPickemTable ? 'osratingCell' : 'evCell';
      return cellClasses;

    },
  },
  {
    field: "trueOdds",
    headerName: "TRUE ODDS",
    headerAlign: "center",
    flex: 1,
    align: "center",
    sortable: false,
    renderCell(params) {
      if (!isDisplayUnlocked) {
        return <LockIcon />;
      }
      return params.value;
    },
  },
  {
    field: "evAge",
    headerName: "AGE",
    headerAlign: "center",
    flex: 1,
    align: "center",
    sortable: true,
    type: "number",
    renderCell(params) {
      // evAgeLabel is a shared component and is expecting the time string to not include the ending Z so strip it off here.
      let age = params.row.evAge;
      if (age && age[age.length - 1] === 'Z') {
        age = age.substring(0, age.length-1);
      }
      return evAgeLabel(age);
    },
  },
  {
    field: "exposure",
    headerName: "EXPOSURE",
    flex: 1,
    headerAlign: "center",
    align: "center",
    sortable: true,
    renderCell(params) {
      let lineupCount = exposureCounts.get("lineupCount");
      if (!lineupCount) {
        lineupCount = 0;
      }
      let count = exposureCounts.get(params.row.playerId);
      if (!count) {
        count = 0;
      }
      let exposure = count !== 0 ? (count / lineupCount) * 100 : 0;
      return `${exposure.toFixed(2)}%`;
    },
  },
  {
    field: "addswap",
    headerName: "ADD/SWAP",
    headerAlign: "center",
    align: "center",
    sortable: false,
    flex: 1,
    renderCell: (params: GridRenderCellParams<IShopOutcome>) => {
      const type = getType(params.row);
      const colorDictionary = {
        Add: "#00954D",
        Swap: "#4395FF",
        Remove: "#EE707E",
      };

      const iconDictionary = {
        Add: !isDisplayUnlocked ? (
          <LockIcon fontSize="small" />
        ) : (
          <AddCircleIcon fontSize="small" />
        ),
        Swap: !isDisplayUnlocked ? (
          <LockIcon fontSize="small" />
        ) : (
          <SwapHorizontalCircleIcon fontSize="small" />
        ),
        Remove: !isDisplayUnlocked ? (
          <LockIcon fontSize="small" />
        ) : (
          <RemoveCircleIcon fontSize="small" />
        ),
      };
      return (
        <Button
          disabled={!isDisplayUnlocked}
          variant="outlined"
          style={{
            width:'85px',
            display: "flex",
            justifyContent: "space-between",
            padding: "6px 10px",
            minWidth: "60px",
            color: colorDictionary[type],
            backgroundColor: alpha(colorDictionary[type], 0.1),
            fontSize: "10px",
            fontWeight: 500,
            border: `1px solid ${colorDictionary[type]}`,
          }}
          endIcon={iconDictionary[type]}
          onClick={() => handleLegClick(params.row as IParlayOutcome)}
        >
          {type}
        </Button>
      );
    },
  },
];
